/**
 * The single error taxonomy for the whole system.
 *
 * SAD §8 specifies one uniform error envelope. Having exactly one taxonomy — mapped to HTTP once,
 * at the edge — is what makes that envelope true everywhere rather than mostly true. No module
 * invents its own error shape.
 */
/** Machine-readable error codes. Clients branch on these, never on message text. */
export const ERROR_CODES = {
    // validation
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    INVALID_AMOUNT: 'INVALID_AMOUNT',
    UNSUPPORTED_CURRENCY: 'UNSUPPORTED_CURRENCY',
    // authentication / authorization
    SIGNATURE_INVALID: 'SIGNATURE_INVALID',
    TIMESTAMP_SKEW: 'TIMESTAMP_SKEW',
    NONCE_REPLAYED: 'NONCE_REPLAYED',
    PARTICIPANT_UNKNOWN: 'PARTICIPANT_UNKNOWN',
    MTLS_REQUIRED: 'MTLS_REQUIRED',
    FORBIDDEN: 'FORBIDDEN',
    INSUFFICIENT_ROLE: 'INSUFFICIENT_ROLE',
    // not found
    TRANSACTION_NOT_FOUND: 'TRANSACTION_NOT_FOUND',
    WITHDRAWAL_NOT_FOUND: 'WITHDRAWAL_NOT_FOUND',
    REVIEW_CASE_NOT_FOUND: 'REVIEW_CASE_NOT_FOUND',
    EVIDENCE_FILE_NOT_FOUND: 'EVIDENCE_FILE_NOT_FOUND',
    OPERATOR_NOT_FOUND: 'OPERATOR_NOT_FOUND',
    SETTLEMENT_NOT_FOUND: 'SETTLEMENT_NOT_FOUND',
    // conflict
    OUT_OF_STOCK: 'OUT_OF_STOCK',
    ILLEGAL_TRANSITION: 'ILLEGAL_TRANSITION',
    VERSION_CONFLICT: 'VERSION_CONFLICT',
    IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
    IDEMPOTENT_REQUEST_IN_FLIGHT: 'IDEMPOTENT_REQUEST_IN_FLIGHT',
    DUPLICATE_CLIENT_REFERENCE: 'DUPLICATE_CLIENT_REFERENCE',
    ALREADY_RESERVED: 'ALREADY_RESERVED',
    /** An operator acted on a review case currently claimed by a different operator. */
    CASE_CLAIMED: 'CASE_CLAIMED',
    // business rule
    LOCK_REFERENCE_REQUIRED: 'LOCK_REFERENCE_REQUIRED',
    DUPLICATE_TRANS_REF: 'DUPLICATE_TRANS_REF',
    EVIDENCE_ATTEMPTS_EXHAUSTED: 'EVIDENCE_ATTEMPTS_EXHAUSTED',
    RESERVATION_CAP_EXCEEDED: 'RESERVATION_CAP_EXCEEDED',
    /**
     * The depositor's declared sending bank is on `blocked_payer_banks` — a bank whose transfers
     * produce no verifiable slip, so no match from it could ever be settled.
     */
    PAYER_BANK_BLOCKED: 'PAYER_BANK_BLOCKED',
    DEPOSITOR_IN_COOLDOWN: 'DEPOSITOR_IN_COOLDOWN',
    DUAL_CONTROL_VIOLATION: 'DUAL_CONTROL_VIOLATION',
    /**
     * An `APPROVE_ADJUSTED` review decision was proposed where no adjustment is computable: no
     * attested slip, an exact-match amount, a zero-paid slip, or a cross-website match.
     */
    ADJUSTMENT_NOT_APPLICABLE: 'ADJUSTMENT_NOT_APPLICABLE',
    CAPABILITY_DISABLED: 'CAPABILITY_DISABLED',
    OPERATOR_EMAIL_TAKEN: 'OPERATOR_EMAIL_TAKEN',
    /** An admin tried to change their own role/status, or demote the last active admin. */
    OPERATOR_SELF_LOCKOUT: 'OPERATOR_SELF_LOCKOUT',
    // rate limit
    RATE_LIMITED: 'RATE_LIMITED',
    // transient
    PROVIDER_UNAVAILABLE: 'PROVIDER_UNAVAILABLE',
    PROVIDER_TIMEOUT: 'PROVIDER_TIMEOUT',
    CIRCUIT_OPEN: 'CIRCUIT_OPEN',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
};
/** HTTP status for each error category (SAD §8). */
const STATUS_BY_TYPE = {
    validation: 400,
    authentication: 401,
    authorization: 403,
    not_found: 404,
    conflict: 409,
    business_rule: 422,
    rate_limited: 429,
    transient: 503,
};
/**
 * Every `ErrorType` value, in one runtime array.
 *
 * `ErrorType` itself is a compile-time-only union, so anything that needs the values at runtime
 * (e.g. `@pf/contracts` building a Zod enum) reuses this rather than re-declaring the literals.
 */
export const ERROR_TYPES = Object.keys(STATUS_BY_TYPE);
/**
 * Whether a category is safe for the client to retry unchanged.
 *
 * Only transient faults and rate limits are. A 409 conflict is *not* retryable unchanged —
 * the client must reload and re-evaluate (SAD §10 retry classification).
 */
const RETRYABLE_BY_TYPE = {
    validation: false,
    authentication: false,
    authorization: false,
    not_found: false,
    conflict: false,
    business_rule: false,
    rate_limited: true,
    transient: true,
};
/**
 * The base error for every business failure in the system.
 *
 * Carries everything the edge needs to render the SAD §8 envelope, so the HTTP layer performs
 * no interpretation of its own.
 */
export class ApplicationError extends Error {
    type;
    code;
    details;
    constructor(type, code, message, details = {}) {
        super(message);
        this.name = 'ApplicationError';
        this.type = type;
        this.code = code;
        this.details = details;
    }
    /** HTTP status this error maps to. */
    get status() {
        return STATUS_BY_TYPE[this.type];
    }
    /** Whether the client may retry the identical request. */
    get retryable() {
        return RETRYABLE_BY_TYPE[this.type];
    }
    /** Render the SAD §8 uniform error envelope. */
    toEnvelope(requestId) {
        return {
            error: {
                code: this.code,
                type: this.type,
                message: this.message,
                retryable: this.retryable,
                request_id: requestId,
                ...(Object.keys(this.details).length > 0 ? { details: this.details } : {}),
            },
        };
    }
}
/** No AVAILABLE withdrawal exists at the requested amount (SAD §8, 409). */
export class OutOfStockError extends ApplicationError {
    constructor(amount, currency) {
        super('conflict', ERROR_CODES.OUT_OF_STOCK, 'No available withdrawal at that amount.', {
            amount,
            currency,
        });
        this.name = 'OutOfStockError';
    }
}
/** A state machine was asked to make a transition it does not permit. */
export class IllegalTransitionError extends ApplicationError {
    constructor(aggregate, from, event) {
        super('conflict', ERROR_CODES.ILLEGAL_TRANSITION, `${aggregate} cannot handle "${event}" while ${from}.`, { aggregate, from, event });
        this.name = 'IllegalTransitionError';
    }
}
/**
 * A guarded update affected zero rows — the aggregate changed underneath us.
 *
 * The caller reloads and re-evaluates (SAD §4). This is expected under concurrency, not a fault.
 */
export class VersionConflictError extends ApplicationError {
    constructor(aggregate, id) {
        super('conflict', ERROR_CODES.VERSION_CONFLICT, `${aggregate} ${id} was modified concurrently; reload and retry.`, { aggregate, id });
        this.name = 'VersionConflictError';
    }
}
/** A withdrawal was submitted without proof that the website locked the funds (SAD §8, 422). */
export class LockReferenceRequiredError extends ApplicationError {
    constructor() {
        super('business_rule', ERROR_CODES.LOCK_REFERENCE_REQUIRED, 'PaymentFlow will not enqueue an unlocked withdrawal.');
        this.name = 'LockReferenceRequiredError';
    }
}
/** A checker attempted to confirm their own proposal (SAD §11 dual control). */
export class DualControlViolationError extends ApplicationError {
    constructor(operatorId) {
        super('business_rule', ERROR_CODES.DUAL_CONTROL_VIOLATION, 'The confirming operator must differ from the proposing operator.', { operatorId });
        this.name = 'DualControlViolationError';
    }
}
/** Type guard for the application error family. */
export function isApplicationError(value) {
    return value instanceof ApplicationError;
}
//# sourceMappingURL=errors.js.map